import { useTranslation } from "react-i18next";
import clsx from "clsx";
import type { InvoiceSendStatus } from "types/Invoice";

export type SendFilter = "all" | InvoiceSendStatus["status"];

// The chips, left to right. Each shows how many of the loaded rows it would keep.
const FILTERS: Array<{ value: SendFilter; labelKey: string; label: string }> = [
  { value: "all", labelKey: "invoice_send_filter_all", label: "All" },
  { value: "not_sent", labelKey: "invoice_send_filter_not_sent", label: "Not sent" },
  { value: "sent", labelKey: "invoice_send_filter_sent", label: "Sent" },
  { value: "failed", labelKey: "invoice_send_filter_failed", label: "Failed" },
];

export function filterBySendStatus<T extends { send: InvoiceSendStatus }>(
  rows: T[],
  filter: SendFilter
): T[] {
  return filter === "all" ? rows : rows.filter((row) => row.send.status === filter);
}

type Props = {
  rows: Array<{ send: InvoiceSendStatus }>;
  value: SendFilter;
  onChange: (filter: SendFilter) => void;
};

export default function SendFilterChips({ rows, value, onChange }: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-1.5" role="group">
      {FILTERS.map((filter) => (
        <button
          key={filter.value}
          type="button"
          aria-pressed={value === filter.value}
          onClick={() => onChange(filter.value)}
          className={clsx(
            "rounded-full px-3 py-1 text-sm transition-colors max-md:min-h-11 max-md:px-4",
            value === filter.value
              ? "bg-indigo-600 text-white"
              : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200"
          )}
        >
          {t(filter.labelKey, { defaultValue: filter.label })} (
          {filterBySendStatus(rows, filter.value).length})
        </button>
      ))}
    </div>
  );
}
